<script lang="ts" setup>
import { ref, computed, nextTick } from 'vue'
import { CanonicalSuggestion } from '@/app/core/services/bank-templates'

/**
 * Editor YAML asistido, con tema claro:
 * - resalta las claves en un color distinto al de los valores
 * - dibuja guias verticales de indentacion cada 2 espacios
 * - sugiere rutas del Canonical Model al escribir dentro de {{...}}
 *
 * Implementacion: un <textarea> transparente (recibe el teclado y el caret)
 * superpuesto a un <pre> con el mismo texto resaltado. Ambos comparten
 * metricas de fuente y se sincroniza el scroll.
 */
const props = withDefaults(
  defineProps<{
    modelValue: string
    suggestions: CanonicalSuggestion[]
    readonly?: boolean
    height?: string
  }>(),
  {
    readonly: false,
    height: '520px'
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

/* Altura de linea fija (leading-6 = 24px): alinea gutter, pre y textarea */
const LINE_HEIGHT = 24
/* Padding interno compartido por el gutter y el area de texto */
const PADDING_Y = 12
const PADDING_X = 14

const textareaEl = ref<HTMLTextAreaElement | null>(null)
const highlightEl = ref<HTMLElement | null>(null)
const gutterEl = ref<HTMLElement | null>(null)

/* ------------------------------------------------------------------------ */
/* Resaltado de sintaxis                                                     */
/* ------------------------------------------------------------------------ */

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

/* Colorea las {{...}} dentro de un texto ya escapado */
const markExpressions = (escaped: string): string =>
  escaped.replace(
    /\{\{[^}]*\}?\}?/g,
    match => `<span class="tok-expr">${match}</span>`
  )

/* Resalta la porcion de valor (despues de "clave:") de una linea escapada */
const highlightValue = (escaped: string): string => {
  let html = ''
  let rest = escaped
  const pattern =
    /("[^"]*")|('[^']*')|(#.*$)|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?)/
  while (rest.length > 0) {
    const match = rest.match(pattern)
    if (!match || match.index === undefined) {
      html += rest
      break
    }
    html += rest.slice(0, match.index)
    const token = match[0]
    if (match[1] || match[2]) {
      html += `<span class="tok-str">${markExpressions(token)}</span>`
    } else if (match[3]) {
      html += `<span class="tok-comment">${token}</span>`
      rest = ''
      html += ''
      break
    } else if (match[4]) {
      html += `<span class="tok-bool">${token}</span>`
    } else {
      html += `<span class="tok-num">${token}</span>`
    }
    rest = rest.slice(match.index + token.length)
  }
  return html
}

/* Convierte la indentacion en spans con guia vertical cada 2 espacios */
const indentGuides = (spaces: string): string => {
  let html = ''
  let index = 0
  while (index + 2 <= spaces.length) {
    html += '<span class="indent-guide">  </span>'
    index += 2
  }
  if (index < spaces.length) html += ' '
  return html
}

const highlightLine = (line: string): string => {
  const indentMatch = line.match(/^( *)/)
  const indent = indentMatch ? indentMatch[1] : ''
  let rest = line.slice(indent.length)
  let html = indentGuides(indent)

  /* Item de lista: el "- " se pinta como puntuacion */
  if (rest.startsWith('- ')) {
    html += '<span class="tok-dash">-</span> '
    rest = rest.slice(2)
  }

  /* Comentario de linea completa */
  if (rest.startsWith('#')) {
    return html + `<span class="tok-comment">${escapeHtml(rest)}</span>`
  }

  /* clave: valor */
  const keyMatch = rest.match(/^([A-Za-z0-9_.-]+)(:)(\s|$)/)
  if (keyMatch) {
    html += `<span class="tok-key">${escapeHtml(keyMatch[1])}</span>`
    html += '<span class="tok-dash">:</span>'
    const value = rest.slice(keyMatch[1].length + 1)
    html += highlightValue(escapeHtml(value))
    return html
  }

  return html + highlightValue(escapeHtml(rest))
}

const highlighted = computed(() =>
  props.modelValue
    .split('\n')
    .map(line => highlightLine(line) || '&nbsp;')
    .join('\n')
)

const lineNumbers = computed(() => {
  const count = props.modelValue.split('\n').length
  return Array.from({ length: count }, (_, index) => index + 1)
})

/* ------------------------------------------------------------------------ */
/* Sincronizacion de scroll                                                  */
/* ------------------------------------------------------------------------ */

const onScroll = () => {
  const textarea = textareaEl.value
  if (!textarea) return
  if (highlightEl.value) {
    highlightEl.value.scrollTop = textarea.scrollTop
    highlightEl.value.scrollLeft = textarea.scrollLeft
  }
  if (gutterEl.value) gutterEl.value.scrollTop = textarea.scrollTop
  closeSuggestions()
}

/* ------------------------------------------------------------------------ */
/* Edicion: Tab de 2 espacios y auto-indentacion en Enter                    */
/* ------------------------------------------------------------------------ */

const setValue = (value: string, caret: number) => {
  emit('update:modelValue', value)
  void nextTick(() => {
    const textarea = textareaEl.value
    if (!textarea) return
    textarea.selectionStart = caret
    textarea.selectionEnd = caret
  })
}

const insertAtCaret = (insert: string) => {
  const textarea = textareaEl.value
  if (!textarea) return
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const value =
    props.modelValue.slice(0, start) + insert + props.modelValue.slice(end)
  setValue(value, start + insert.length)
}

/* ------------------------------------------------------------------------ */
/* Autocompletado del Canonical Model dentro de {{...}}                      */
/* ------------------------------------------------------------------------ */

const suggestionsOpen = ref(false)
const activeSuggestion = ref(0)
const suggestionPrefix = ref('')
const dropdownTop = ref(0)
const dropdownLeft = ref(0)

const filteredSuggestions = computed(() => {
  const prefix = suggestionPrefix.value.toLowerCase()
  const startsWith = props.suggestions.filter(item =>
    item.value.toLowerCase().startsWith(prefix)
  )
  if (startsWith.length > 0) return startsWith.slice(0, 12)
  return props.suggestions
    .filter(item => item.value.toLowerCase().includes(prefix))
    .slice(0, 12)
})

const closeSuggestions = () => {
  suggestionsOpen.value = false
  activeSuggestion.value = 0
}

/* Posicion en pixeles del caret, midiendo un espejo del contenido */
const caretCoordinates = (): { top: number; left: number } => {
  const textarea = textareaEl.value
  if (!textarea) return { top: 0, left: 0 }
  const mirror = document.createElement('div')
  const style = window.getComputedStyle(textarea)
  ;[
    'fontFamily',
    'fontSize',
    'lineHeight',
    'letterSpacing',
    'tabSize'
  ].forEach(prop => {
    mirror.style[prop as any] = style[prop as any]
  })
  mirror.style.position = 'absolute'
  mirror.style.visibility = 'hidden'
  mirror.style.whiteSpace = 'pre'
  mirror.style.padding = `${PADDING_Y}px ${PADDING_X}px`
  const before = props.modelValue.slice(0, textarea.selectionStart)
  mirror.textContent = before
  const marker = document.createElement('span')
  marker.textContent = '​'
  mirror.appendChild(marker)
  document.body.appendChild(mirror)
  const top = marker.offsetTop - textarea.scrollTop
  const left = marker.offsetLeft - textarea.scrollLeft
  document.body.removeChild(mirror)
  return { top, left }
}

/* Revisa si el caret esta dentro de una {{...}} abierta y abre el dropdown */
const updateSuggestions = () => {
  if (props.readonly) return
  const textarea = textareaEl.value
  if (!textarea) return
  const before = props.modelValue.slice(0, textarea.selectionStart)
  const open = before.lastIndexOf('{{')
  if (open === -1) return closeSuggestions()
  const fragment = before.slice(open + 2)
  if (fragment.includes('}') || fragment.includes('\n'))
    return closeSuggestions()
  if (!/^[\w.(]*$/.test(fragment)) return closeSuggestions()

  suggestionPrefix.value = fragment
  if (filteredSuggestions.value.length === 0) return closeSuggestions()

  const caret = caretCoordinates()
  dropdownTop.value = caret.top + LINE_HEIGHT
  dropdownLeft.value = Math.max(0, caret.left - 8)
  activeSuggestion.value = 0
  suggestionsOpen.value = true
}

const acceptSuggestion = (index?: number) => {
  const textarea = textareaEl.value
  const suggestion =
    filteredSuggestions.value[index ?? activeSuggestion.value]
  if (!textarea || !suggestion) return
  const caret = textarea.selectionStart
  const start = caret - suggestionPrefix.value.length
  let insert = suggestion.value
  /* Cierra las llaves si no es una funcion y no hay un }} inmediato */
  const next = props.modelValue.slice(caret, caret + 2)
  if (!insert.endsWith('(') && next !== '}}') insert += '}}'
  const value =
    props.modelValue.slice(0, start) + insert + props.modelValue.slice(caret)
  setValue(value, start + insert.length)
  closeSuggestions()
}

/* ------------------------------------------------------------------------ */
/* Eventos                                                                   */
/* ------------------------------------------------------------------------ */

const onInput = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value)
  void nextTick(() => updateSuggestions())
}

const onKeydown = (event: KeyboardEvent) => {
  if (props.readonly) return

  if (suggestionsOpen.value) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      activeSuggestion.value =
        (activeSuggestion.value + 1) % filteredSuggestions.value.length
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      activeSuggestion.value =
        (activeSuggestion.value - 1 + filteredSuggestions.value.length) %
        filteredSuggestions.value.length
      return
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      acceptSuggestion()
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      closeSuggestions()
      return
    }
  }

  if (event.key === 'Tab') {
    event.preventDefault()
    insertAtCaret('  ')
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    const textarea = textareaEl.value
    if (!textarea) return
    const before = props.modelValue.slice(0, textarea.selectionStart)
    const currentLine = before.slice(before.lastIndexOf('\n') + 1)
    const indent = currentLine.match(/^ */)?.[0] ?? ''
    /* Si la linea abre un bloque (termina en ":") se indenta un nivel mas */
    const extra = currentLine.trimEnd().endsWith(':') ? '  ' : ''
    insertAtCaret('\n' + indent + extra)
  }
}

const onClick = () => {
  void nextTick(() => updateSuggestions())
}

const onBlur = () => {
  /* Pequena espera para que el click en una sugerencia alcance a ejecutarse */
  setTimeout(() => closeSuggestions(), 150)
}

/* Lleva el editor a una linea (para "ir a la linea" desde la validacion) */
const focusLine = (line: number) => {
  const textarea = textareaEl.value
  if (!textarea) return
  const lines = props.modelValue.split('\n')
  const position = lines
    .slice(0, Math.max(0, line - 1))
    .reduce((sum, item) => sum + item.length + 1, 0)
  textarea.focus()
  textarea.selectionStart = position
  textarea.selectionEnd = position
  textarea.scrollTop = Math.max(0, (line - 4) * LINE_HEIGHT)
  onScroll()
}

defineExpose({ focusLine })
</script>
<template>
  <div
    class="relative border border-gray-200 rounded-xl bg-white overflow-hidden flex"
    :style="{ height }">
    <!-- Numeros de linea -->
    <div
      ref="gutterEl"
      class="shrink-0 bg-gray-50 border-r border-gray-100 text-right select-none overflow-hidden"
      :style="{ paddingTop: PADDING_Y + 'px', width: '52px' }">
      <div
        v-for="line in lineNumbers"
        :key="line"
        class="text-xs text-gray-400 font-mono pr-3"
        :style="{ height: LINE_HEIGHT + 'px', lineHeight: LINE_HEIGHT + 'px' }">
        {{ line }}
      </div>
    </div>

    <!-- Area de edicion -->
    <div class="relative flex-1 min-w-0">
      <pre
        ref="highlightEl"
        aria-hidden="true"
        class="editor-layer text-gray-800 pointer-events-none"
        v-html="highlighted" />
      <textarea
        ref="textareaEl"
        class="editor-layer editor-input scroll"
        :value="modelValue"
        :readonly="readonly"
        spellcheck="false"
        autocomplete="off"
        autocapitalize="off"
        wrap="off"
        @input="onInput"
        @keydown="onKeydown"
        @scroll="onScroll"
        @click="onClick"
        @blur="onBlur" />

      <!-- Dropdown de sugerencias del Canonical Model -->
      <div
        v-if="suggestionsOpen"
        class="absolute z-20 w-96 max-w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
        :style="{ top: dropdownTop + 'px', left: dropdownLeft + 'px' }">
        <button
          v-for="(item, index) in filteredSuggestions"
          :key="item.value"
          type="button"
          class="w-full flex items-center justify-between gap-3 px-3 py-1.5 text-left"
          :class="
            index === activeSuggestion
              ? 'bg-blue-50 text-blue-700'
              : 'text-gray-700 hover:bg-gray-50'
          "
          @mousedown.prevent="acceptSuggestion(index)"
          @mouseenter="activeSuggestion = index">
          <span class="font-mono text-xs truncate">{{ item.value }}</span>
          <span class="text-xs text-gray-400 shrink-0">{{ item.hint }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped>
.editor-layer {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 12px 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 24px;
  white-space: pre;
  overflow: hidden;
  tab-size: 2;
}

.editor-input {
  background: transparent;
  color: transparent;
  caret-color: #111827;
  resize: none;
  border: none;
  outline: none;
  overflow: auto;
}

.editor-input::selection {
  background: rgba(59, 130, 246, 0.2);
}

/* Claves en color propio, para diferenciarlas del valor */
:deep(.tok-key) {
  color: #1d4ed8;
  font-weight: 600;
}
:deep(.tok-dash) {
  color: #9ca3af;
}
:deep(.tok-str) {
  color: #047857;
}
:deep(.tok-expr) {
  color: #7c3aed;
  font-weight: 600;
}
:deep(.tok-num) {
  color: #b45309;
}
:deep(.tok-bool) {
  color: #be123c;
}
:deep(.tok-comment) {
  color: #9ca3af;
  font-style: italic;
}

/* Guia vertical por cada nivel de indentacion (2 espacios) */
:deep(.indent-guide) {
  box-shadow: inset 1px 0 0 #e5e7eb;
}
</style>
